package com.sweetlime.bingeamigo.repository.impl

import com.sweetlime.bingeamigo.model.LibraryEpisode
import com.sweetlime.bingeamigo.model.LibraryMovie
import com.sweetlime.bingeamigo.model.LibraryTvShow
import com.sweetlime.bingeamigo.model.Movie
import com.sweetlime.bingeamigo.model.TvShow
import com.sweetlime.bingeamigo.model.UserActivity
import com.sweetlime.bingeamigo.model.toLibraryMovie
import com.sweetlime.bingeamigo.model.types.ActivityType
import com.sweetlime.bingeamigo.repository.ActivityRepository
import com.sweetlime.bingeamigo.repository.AuthRepository
import com.sweetlime.bingeamigo.repository.LibraryRepository
import com.sweetlime.bingeamigo.repository.UserRepository
import com.sweetlime.bingeamigo.service.XpService
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.QuerySnapshot
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.map
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

class LibraryRepositoryImpl(
    private val firestore: FirebaseFirestore,
    private val activityRepository: ActivityRepository,
    private val authRepository: AuthRepository,
    private val userRepository: UserRepository,
    private val xpService: XpService,
) : LibraryRepository {
    private val auth by lazy { Firebase.auth }

    private val uid: String
        get() = auth.currentUser?.uid ?: throw IllegalStateException("User not logged in")

    private fun moviesCollection() = firestore
        .collection("users")
        .document(uid)
        .collection("movies")

    private fun moviesCollection(userId: String) = firestore
        .collection("users")
        .document(userId)
        .collection("movies")

    private fun tvShowsCollection() = firestore
        .collection("users")
        .document(uid)
        .collection("tvshows")

    private fun showTrackersCollection() = firestore.collection("showTrackers")

    private suspend fun upsertShowTrackerIndex(showId: String, showName: String, userId: String) {
        val showRef = showTrackersCollection().document(showId)
        val trackerRef = showRef.collection("trackers").document(userId)
        runCatching {
            firestore.runTransaction {
                val snap = get(showRef)
                if (snap.exists) {
                    val current = runCatching {
                        snap.get<Long>("trackerCount") ?: 0L
                    }.getOrDefault(0L)
                    update(showRef, "trackerCount" to current + 1, "showName" to showName)
                } else {
                    set(showRef, mapOf(
                        "tmdbShowId" to showId,
                        "showName" to showName,
                        "trackerCount" to 1L
                    ))
                }
                set(trackerRef, mapOf(
                    "userId" to userId,
                    "addedAt" to Timestamp.now()
                ))
            }
        }
    }

    private suspend fun removeShowTrackerIndex(showId: String, userId: String) {
        val showRef = showTrackersCollection().document(showId)
        val trackerRef = showRef.collection("trackers").document(userId)
        runCatching {
            firestore.runTransaction {
                val snap = get(showRef)
                if (snap.exists) {
                    val current = runCatching {
                        snap.get<Long>("trackerCount") ?: 1L
                    }.getOrDefault(1L)
                    val newCount = maxOf(0L, current - 1)
                    if (newCount == 0L) {
                        delete(showRef)
                    } else {
                        update(showRef, "trackerCount" to newCount)
                    }
                }
                delete(trackerRef)
            }
        }
    }

    // --- Flows ---

    override fun getWatchlist(): Flow<List<LibraryMovie>> =
        moviesCollection().where { "watchlist" equalTo true }.snapshots().mapToMovies()

    override fun getWatched(): Flow<List<LibraryMovie>> =
        moviesCollection().where { "watched" equalTo true }.snapshots().mapToMovies()

    override fun getFavorites(): Flow<List<LibraryMovie>> =
        moviesCollection().where { "favorite" equalTo true }.snapshots().mapToMovies()

    private fun Flow<QuerySnapshot>.mapToMovies(): Flow<List<LibraryMovie>> =
        map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                runCatching { doc.data<LibraryMovie>() }.getOrNull()
            }
        }.catch { emit(emptyList()) }

    // --- Other users (one-time fetch) ---

    override suspend fun getUserWatchlist(userId: String): List<LibraryMovie> =
        fetchUserMovies(userId, "watchlist")

    override suspend fun getUserWatched(userId: String): List<LibraryMovie> =
        fetchUserMovies(userId, "watched")

    override suspend fun getUserFavorites(userId: String): List<LibraryMovie> =
        fetchUserMovies(userId, "favorite")

    private suspend fun fetchUserMovies(userId: String, flag: String): List<LibraryMovie> =
        runCatching {
            moviesCollection(userId)
                .where { flag equalTo true }
                .get()
                .documents
                .mapNotNull { runCatching { it.data<LibraryMovie>() }.getOrNull() }
        }.getOrDefault(emptyList())

    // --- Helpers ---

    // Reads the existing document or creates a fresh one — preserves flags we're not touching.
    private suspend fun getOrCreate(libraryMovie: LibraryMovie): LibraryMovie {
        val existing = runCatching {
            moviesCollection().document(libraryMovie.id.toString()).get().data<LibraryMovie>()
        }.getOrNull()
        return existing ?: libraryMovie
    }

    // Deletes the document only when there is nothing left to keep — no flags and no user data.
    private suspend fun save(movieId: String, entry: LibraryMovie) {
        val ref = moviesCollection().document(movieId)
        val hasUserData = entry.userRating > 0.0 || entry.userReview != null
        if (!entry.watchlist && !entry.watched && !entry.favorite && !hasUserData) {
            ref.delete()
        } else {
            ref.set(entry)
        }
    }

    // --- Activity ---

    @OptIn(ExperimentalTime::class)
    private suspend fun publishActivity(movie: Movie, type: ActivityType) {
        val userId = authRepository.currentUserId() ?: return
        val user = userRepository.getUserProfile(userId) ?: return
        activityRepository.publishActivity(
            UserActivity(
                id = "${userId}_${type.value}_${movie.id}",
                userId = user.id,
                username = user.username,
                userAvatar = user.avatarUrl,
                type = type.value,
                movieId = movie.id,
                movieTitle = movie.title,
                moviePoster = movie.posterPath.orEmpty(),
                timestamp = Clock.System.now().toEpochMilliseconds()
            )
        )
    }

    // --- Watchlist ---

    override suspend fun addToWatchlist(libraryMovie: LibraryMovie) {
        val updated = getOrCreate(libraryMovie).copy(watchlist = true, addedAt = Timestamp.now())
        save(libraryMovie.id.toString(), updated)
        publishActivity(libraryMovie.movie, ActivityType.ADD_WATCHLIST)
        val userId = authRepository.currentUserId() ?: return
        xpService.awardXp(userId, XpService.ACTION_WATCHLIST_ADD_MOVIE, libraryMovie.id.toString(), XpService.XP_WATCHLIST_ADD_MOVIE)
    }

    override suspend fun removeFromWatchlist(movieId: String) {
        val current = runCatching {
            moviesCollection().document(movieId).get().data<LibraryMovie>()
        }.getOrNull() ?: return
        save(movieId, current.copy(watchlist = false))
    }

    // --- Watched ---

    override suspend fun addToWatched(libraryMovie: LibraryMovie) {
        // Marking as watched also clears watchlist (same behaviour as before)
        val updated = getOrCreate(libraryMovie).copy(watched = true, watchedAt = Timestamp.now(), watchlist = false)
        save(libraryMovie.id.toString(), updated)
        publishActivity(libraryMovie.movie, ActivityType.MARK_WATCHED)
        val userId = authRepository.currentUserId() ?: return
        xpService.awardXp(userId, XpService.ACTION_WATCHED_MOVIE, libraryMovie.id.toString(), XpService.XP_WATCHED_MOVIE)
    }

    override suspend fun removeFromWatched(movieId: String) {
        val current = runCatching {
            moviesCollection().document(movieId).get().data<LibraryMovie>()
        }.getOrNull() ?: return
        save(movieId, current.copy(watched = false, watchedAt = null))
    }

    // --- Favorites ---

    override suspend fun addToFavorites(libraryMovie: LibraryMovie) {
        val updated = getOrCreate(libraryMovie).copy(favorite = true, favoriteAt = Timestamp.now())
        save(libraryMovie.id.toString(), updated)
        publishActivity(libraryMovie.movie, ActivityType.FAVORITE_MOVIE)
        val userId = authRepository.currentUserId() ?: return
        xpService.awardXp(userId, XpService.ACTION_FAVORITE_MOVIE, libraryMovie.id.toString(), XpService.XP_FAVORITE_MOVIE)
    }

    override suspend fun removeFromFavorites(movieId: String) {
        val current = runCatching {
            moviesCollection().document(movieId).get().data<LibraryMovie>()
        }.getOrNull() ?: return
        save(movieId, current.copy(favorite = false, favoriteAt = null))
    }

    // =========================================================
    // TV Shows
    // =========================================================

    private fun Flow<QuerySnapshot>.mapToTvShows(): Flow<List<LibraryTvShow>> =
        map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                runCatching { doc.data<LibraryTvShow>() }.getOrNull()
            }
        }.catch { emit(emptyList()) }

    private suspend fun getOrCreateTvShow(libraryTvShow: LibraryTvShow): LibraryTvShow {
        val existing = runCatching {
            tvShowsCollection().document(libraryTvShow.id.toString()).get().data<LibraryTvShow>()
        }.getOrNull()
        return existing ?: libraryTvShow
    }

    private suspend fun saveTvShow(tvShowId: String, entry: LibraryTvShow) {
        val ref = tvShowsCollection().document(tvShowId)
        val hasUserData = entry.userRating > 0.0 || entry.userReview != null
        val hasEpisodes = entry.episodes.any { it.watched }
        if (!entry.tracking && !entry.completed && !entry.favorite && !hasUserData && !hasEpisodes) {
            ref.delete()
        } else {
            ref.set(entry)
        }
    }

    @OptIn(ExperimentalTime::class)
    private suspend fun publishTvActivity(libraryTvShow: LibraryTvShow, type: ActivityType) {
        val userId = authRepository.currentUserId() ?: return
        val user = userRepository.getUserProfile(userId) ?: return
        activityRepository.publishActivity(
            UserActivity(
                id = "${userId}_${type.value}_${libraryTvShow.id}",
                userId = user.id,
                username = user.username,
                userAvatar = user.avatarUrl,
                type = type.value,
                tvShowId = libraryTvShow.tvShow.id,
                tvShowTitle = libraryTvShow.tvShow.name,
                tvShowPoster = libraryTvShow.tvShow.posterPath.orEmpty(),
                timestamp = Clock.System.now().toEpochMilliseconds()
            )
        )
    }

    override fun getTvShowTracking(): Flow<List<LibraryTvShow>> =
        tvShowsCollection().where { "tracking" equalTo true }.snapshots().mapToTvShows()

    override fun getTvShowCompleted(): Flow<List<LibraryTvShow>> =
        tvShowsCollection().where { "completed" equalTo true }.snapshots().mapToTvShows()

    override fun getTvShowFavorites(): Flow<List<LibraryTvShow>> =
        tvShowsCollection().where { "favorite" equalTo true }.snapshots().mapToTvShows()

    override suspend fun addTvShowToTracking(libraryTvShow: LibraryTvShow) {
        val updated = getOrCreateTvShow(libraryTvShow).copy(tracking = true, addedAt = Timestamp.now())
        saveTvShow(libraryTvShow.id.toString(), updated)
        publishTvActivity(libraryTvShow, ActivityType.ADD_SHOW_TRACKING)
        val userId = authRepository.currentUserId() ?: return
        xpService.awardXp(userId, XpService.ACTION_TRACKING_ADD_SHOW, libraryTvShow.id.toString(), XpService.XP_TRACKING_ADD_SHOW)
        upsertShowTrackerIndex(libraryTvShow.id.toString(), libraryTvShow.tvShow.name, userId)
    }

    override suspend fun removeFromTvShowTracking(tvShowId: String) {
        val current = runCatching {
            tvShowsCollection().document(tvShowId).get().data<LibraryTvShow>()
        }.getOrNull() ?: return
        saveTvShow(tvShowId, current.copy(tracking = false))
        val userId = authRepository.currentUserId() ?: return
        removeShowTrackerIndex(tvShowId, userId)
    }

    override suspend fun removeFromTvShowCompleted(tvShowId: String) {
        val current = runCatching {
            tvShowsCollection().document(tvShowId).get().data<LibraryTvShow>()
        }.getOrNull() ?: return
        saveTvShow(tvShowId, current.copy(completed = false, completedAt = null))
    }

    override suspend fun addTvShowToFavorites(libraryTvShow: LibraryTvShow) {
        val updated = getOrCreateTvShow(libraryTvShow).copy(favorite = true, favoriteAt = Timestamp.now())
        saveTvShow(libraryTvShow.id.toString(), updated)
        publishTvActivity(libraryTvShow, ActivityType.FAVORITE_SHOW)
        val userId = authRepository.currentUserId() ?: return
        xpService.awardXp(userId, XpService.ACTION_FAVORITE_SHOW, libraryTvShow.id.toString(), XpService.XP_FAVORITE_SHOW)
    }

    override suspend fun removeFromTvShowFavorites(tvShowId: String) {
        val current = runCatching {
            tvShowsCollection().document(tvShowId).get().data<LibraryTvShow>()
        }.getOrNull() ?: return
        saveTvShow(tvShowId, current.copy(favorite = false, favoriteAt = null))
    }

    // =========================================================
    // Single-item fetch
    // =========================================================

    override fun getLibraryMovie(movieId: String): Flow<LibraryMovie?> =
        moviesCollection().document(movieId).snapshots()
            .map { snap -> runCatching { snap.takeIf { it.exists }?.data<LibraryMovie>() }.getOrNull() }
            .catch { emit(null) }

    override fun getLibraryTvShow(tvShowId: String): Flow<LibraryTvShow?> =
        tvShowsCollection().document(tvShowId).snapshots()
            .map { snap -> runCatching { snap.takeIf { it.exists }?.data<LibraryTvShow>() }.getOrNull() }
            .catch { emit(null) }

    // =========================================================
    // Rating / Review
    // =========================================================

    override suspend fun rateMovie(movie: LibraryMovie, rating: Double, review: String?) {
        val updated = getOrCreate(movie).copy(userRating = rating, userReview = review)
        save(movie.id.toString(), updated)
    }

    override suspend fun rateTvShow(tvShow: LibraryTvShow, rating: Double, review: String?) {
        val updated = getOrCreateTvShow(tvShow).copy(userRating = rating, userReview = review)
        saveTvShow(tvShow.id.toString(), updated)
    }

    override suspend fun rateEpisode(tvShow: LibraryTvShow, episodeId: Int, rating: Double, review: String?) {
        val tvShowId = tvShow.id.toString()
        val current = getOrCreateTvShow(tvShow)
        val existing = current.episodes.find { it.id == episodeId }
        val updated = existing?.copy(userRating = rating, userReview = review)
            ?: LibraryEpisode(id = episodeId, userRating = rating, userReview = review)
        val updatedEpisodes = current.episodes.filter { it.id != episodeId }.plus(updated)
        saveTvShow(tvShowId, current.copy(episodes = updatedEpisodes))
    }

    // =========================================================
    // Episodes
    // =========================================================

    override fun getWatchedEpisodes(tvShowId: String): Flow<List<LibraryEpisode>> =
        tvShowsCollection().document(tvShowId).snapshots()
            .map { snapshot ->
                runCatching { snapshot.data<LibraryTvShow>().episodes }.getOrDefault(emptyList())
            }.catch { emit(emptyList()) }

    @OptIn(ExperimentalTime::class)
    override suspend fun markEpisodeWatched(tvShow: TvShow, episode: LibraryEpisode, totalEpisodesInShow: Int, episodeName: String, episodeStillPath: String) {
        val tvShowId = tvShow.id.toString()
        val stub = LibraryTvShow(id = tvShow.id, tvShow = tvShow)
        val current = getOrCreateTvShow(stub)
        val updatedEpisodes = current.episodes
            .filter { it.id != episode.id }
            .plus(episode.copy(watched = true))

        val watchedCount = updatedEpisodes.count { it.watched }
        val isNowCompleted = totalEpisodesInShow > 0 && watchedCount >= totalEpisodesInShow

        val now = Timestamp.now()
        val updatedShow = if (isNowCompleted) {
            current.copy(episodes = updatedEpisodes, completed = true, tracking = false, completedAt = now, lastMarkedAt = now)
        } else {
            current.copy(episodes = updatedEpisodes, tracking = true, completed = false, lastMarkedAt = now)
        }
        saveTvShow(tvShowId, updatedShow)

        val userId = authRepository.currentUserId() ?: return
        val entityId = "${tvShow.id}:${episode.id}"
        xpService.awardXp(userId, XpService.ACTION_EPISODE_WATCHED, entityId, XpService.XP_EPISODE_WATCHED)

        val user = userRepository.getUserProfile(userId) ?: return
        activityRepository.publishActivity(
            UserActivity(
                id = "${userId}_${ActivityType.EPISODE_WATCHED.value}_${tvShow.id}_${episode.id}",
                userId = user.id,
                username = user.username,
                userAvatar = user.avatarUrl,
                type = ActivityType.EPISODE_WATCHED.value,
                tvShowId = tvShow.id,
                tvShowTitle = tvShow.name,
                tvShowPoster = tvShow.posterPath.orEmpty(),
                seasonNumber = episode.seasonNumber,
                episodeNumber = episode.episodeNumber,
                episodeId = episode.id,
                episodeName = episodeName,
                episodeStillPath = episodeStillPath,
                timestamp = Clock.System.now().toEpochMilliseconds()
            )
        )

        if (isNowCompleted) {
            xpService.awardXp(userId, XpService.ACTION_SHOW_COMPLETED, tvShowId, XpService.XP_SHOW_COMPLETED)
            publishTvActivity(updatedShow, ActivityType.COMPLETE_SHOW)
        }
    }

    @OptIn(ExperimentalTime::class)
    override suspend fun markAllSeasonEpisodesWatched(
        tvShow: TvShow,
        seasonEpisodes: List<LibraryEpisode>,
        lastEpisode: LibraryEpisode,
        totalEpisodesInShow: Int,
        lastEpisodeName: String,
        lastEpisodeStillPath: String
    ) {
        val tvShowId = tvShow.id.toString()
        val stub = LibraryTvShow(id = tvShow.id, tvShow = tvShow)
        val current = getOrCreateTvShow(stub)

        val episodeIds = seasonEpisodes.map { it.id }.toSet()
        val updatedEpisodes = current.episodes
            .filter { it.id !in episodeIds }
            .plus(seasonEpisodes.map { it.copy(watched = true) })

        val watchedCount = updatedEpisodes.count { it.watched }
        val isNowCompleted = totalEpisodesInShow > 0 && watchedCount >= totalEpisodesInShow

        val now = Timestamp.now()
        val updatedShow = if (isNowCompleted) {
            current.copy(episodes = updatedEpisodes, completed = true, tracking = false, completedAt = now, lastMarkedAt = now)
        } else {
            current.copy(episodes = updatedEpisodes, tracking = true, completed = false, lastMarkedAt = now)
        }
        saveTvShow(tvShowId, updatedShow)

        val userId = authRepository.currentUserId() ?: return
        val entityId = "${tvShow.id}:${lastEpisode.id}"
        xpService.awardXp(userId, XpService.ACTION_EPISODE_WATCHED, entityId, XpService.XP_EPISODE_WATCHED)

        val user = userRepository.getUserProfile(userId) ?: return
        activityRepository.publishActivity(
            UserActivity(
                id = "${userId}_${ActivityType.EPISODE_WATCHED.value}_${tvShow.id}_${lastEpisode.id}",
                userId = user.id,
                username = user.username,
                userAvatar = user.avatarUrl,
                type = ActivityType.EPISODE_WATCHED.value,
                tvShowId = tvShow.id,
                tvShowTitle = tvShow.name,
                tvShowPoster = tvShow.posterPath.orEmpty(),
                seasonNumber = lastEpisode.seasonNumber,
                episodeNumber = lastEpisode.episodeNumber,
                episodeId = lastEpisode.id,
                episodeName = lastEpisodeName,
                episodeStillPath = lastEpisodeStillPath,
                timestamp = Clock.System.now().toEpochMilliseconds()
            )
        )

        if (isNowCompleted) {
            xpService.awardXp(userId, XpService.ACTION_SHOW_COMPLETED, tvShowId, XpService.XP_SHOW_COMPLETED)
            publishTvActivity(updatedShow, ActivityType.COMPLETE_SHOW)
        }
    }

    override suspend fun markEpisodeUnwatched(tvShow: TvShow, episodeId: Int) {
        val tvShowId = tvShow.id.toString()
        val current = runCatching {
            tvShowsCollection().document(tvShowId).get().data<LibraryTvShow>()
        }.getOrNull() ?: return
        val existing = current.episodes.find { it.id == episodeId }
        val updatedEpisodes = current.episodes
            .filter { it.id != episodeId }
            .plus(LibraryEpisode(
                id = episodeId,
                watched = false,
                seasonNumber = existing?.seasonNumber ?: 0,
                episodeNumber = existing?.episodeNumber ?: 0,
                userRating = existing?.userRating ?: 0.0,
                userReview = existing?.userReview
            ))
        val updatedShow = if (current.completed) {
            current.copy(episodes = updatedEpisodes, completed = false, tracking = true, completedAt = null)
        } else {
            current.copy(episodes = updatedEpisodes)
        }
        saveTvShow(tvShowId, updatedShow)
    }
}